import { ReactNode } from 'react';
import { ChatMapMarker } from '../ChatMapView/ChatMapView.types';

/**
 * Primary position of the InfoWindow relative to the marker
 * - top: Above the marker (default)
 * - bottom: Below the marker
 * - left: To the left of the marker
 * - right: To the right of the marker
 */
export type PrimaryPosition = 'top' | 'bottom' | 'left' | 'right';

/**
 * Secondary alignment for fine-tuning position
 * - start: Aligned to top (for left/right) or left (for top/bottom)
 * - center: Centered (default)
 * - end: Aligned to bottom (for left/right) or right (for top/bottom)
 */
export type SecondaryAlignment = 'start' | 'center' | 'end';

/**
 * Complete position configuration for the InfoWindow
 */
export type InfoWindowPosition = {
  primary: PrimaryPosition;
  secondary: SecondaryAlignment;
};

/**
 * Props for the MSmartInfoWindow component
 */
export interface MSmartInfoWindowProps {
  /** The marker data containing position and property info */
  marker: ChatMapMarker;
  /** Reference to the Google Map instance */
  map: google.maps.Map | null;
  /** Callback when the InfoWindow is closed */
  onClose: () => void;
  /** Content to render inside the InfoWindow */
  children: ReactNode;
  /**
   * Element to portal the InfoWindow into. Should be a positioned ancestor of
   * the map (e.g. ChatMapContainer) so the InfoWindow escapes Google Maps'
   * pane stacking context and can sit above filter chips, the list toggle,
   * markers, and clusters.
   */
  portalContainer?: HTMLElement | null;
  /** Whether this is a single property response (uses dot marker instead of price pill) */
  isSinglePropertyResponse?: boolean;
}

/**
 * Props for the styled InfoWindow container
 */
export interface InfoWindowContainerProps {
  $primary: PrimaryPosition;
  $secondary: SecondaryAlignment;
  $markerHalfWidth: number;
  $markerPixelX: number;
  $markerPixelY: number;
  $isSinglePropertyResponse?: boolean;
}

/**
 * Constants for InfoWindow positioning calculations
 */
export const INFO_WINDOW_CONSTANTS = {
  /** Width of the InfoWindow content */
  WIDTH: 304,
  /** Approximate height of the property card */
  HEIGHT: 350,
  /** Height of the price pill marker body */
  MARKER_HEIGHT: 20,
  /** Height of the marker tail/arrow pointing down */
  MARKER_TAIL_HEIGHT: 5,
  /** Width of the price pill marker (approximate) */
  MARKER_WIDTH: 60,
  /** Size of the single property dot marker (circle) */
  DOT_MARKER_SIZE: 18,
  /** Minimum padding from map edges */
  EDGE_PADDING: 16,
  /** Gap between marker and InfoWindow */
  GAP: 4,
} as const;
